import { useQuery } from "@tanstack/vue-query";
import type { Ref } from "vue";

import { explorerApi } from "~/api/client";
import type {
  EvmAddressSummaryInfo,
  EvmErc20TransferInfo,
  EvmTokenSummaryInfo,
  NexonLwBurnHistoryInfo,
  NexonLwBurnHistoryItemInfo,
} from "~/api/repository";
import { UNIT } from "~/environment-variables/unit.const";
import { TokenTransferModel } from "~/types/model/token.model";
import type { IPagination } from "~/types/pagination.type";
import { setComma } from "~/utils/numberFormat";

export type BurnHistoryListView = {
  burnHistoryList: TokenTransferModel[];
  totalCount: string;
  VCNT: string;
  TCNT: string;
};

export const useFetchBurnHistory = (pagination: Ref<IPagination>) => {
  return useQuery({
    queryKey: ["burnHistoryList", pagination],
    queryFn: () =>
      explorerApi.lwController
        .getBurnHistoryList({
          requestBody: {
            page: pagination.value.page,
            size: pagination.value.rowPerPage,
          },
        })
        .then((res) => burnHistoryDto(res)),
  });
};

// API 응답을 BurnHistoryListView로 변환
const burnHistoryDto = (data: NexonLwBurnHistoryInfo): BurnHistoryListView => {
  const vcnt = Number(data.VCNT);
  const tcnt = Number(data.TRXHA); // TRXHA가 실제로는 total count

  const burnHistoryList = data.LIST.map(
    (item) => new TokenTransferModel(convertToEvmErc20TransferInfo(item))
  );

  return {
    burnHistoryList,
    totalCount: setComma(tcnt),
    VCNT: String(vcnt),
    TCNT: String(tcnt),
  };
};

// API 응답 아이템을 TokenTransferModel이 인식하는 형태로 변환
const convertToEvmErc20TransferInfo = (
  item: NexonLwBurnHistoryItemInfo
): EvmErc20TransferInfo => {
  return {
    TRXHA: item.TH,
    MTH: "",
    ADDRSFROMINFO: BURN_FROM_ADDRESS,
    ADDRSTOINFO: BURN_TO_ADDRESS,
    TKNSINFO: BURN_TOKEN_INFO,
    QTY: item.AMT,
    AG: item.AG,
    UT: item.UT,
  };
};

// Burn 관련 고정 정보
const BURN_TOKEN_INFO: EvmTokenSummaryInfo = {
  IMG: "",
  ADDR: "0x0000000000000000000000000000000000000000",
  NN: UNIT,
  SB: UNIT,
};

const BURN_FROM_ADDRESS: EvmAddressSummaryInfo = {
  ADT: "EOA" as EvmAddressSummaryInfo.ADT,
  ADDR: "0x0000000000000000000000000000000000000000",
  NN: "",
  SB: "",
};

const BURN_TO_ADDRESS: EvmAddressSummaryInfo = {
  ADT: "CA" as EvmAddressSummaryInfo.ADT,
  ADDR: "0x000000000000000000000000000000000000dEaD",
  NN: "Burn",
  SB: "",
};
